import { captureException } from '@sentry/core';

import { type EventsMap } from '../types.ts';

type Listener<T> = (payload: T) => void;
type EventListener<Events extends EventsMap, K extends keyof Events> = Listener<
  Events[K]
>;

/**
 * Strongly-typed Event Bus that manages event listeners and dispatches events.
 * Each Event Bus only allows a predefined list of events.
 */
export abstract class EventBus<Events extends EventsMap> {
  #listeners = new Map<keyof Events, Array<EventListener<Events, keyof Events>>>();

  /**
   * Registers a typed callback for a specific event.
   * A listener may not be registered twice (deduped by function reference).
   */
  on<T extends keyof Events>(eventType: T, listener: Listener<Events[T]>): void {
    if (!this.#listeners.has(eventType)) {
      this.#listeners.set(eventType, []);
    }

    // remove the event and listener if they exist, to avoid duplicates
    this.remove(eventType, listener);

    this.#listeners.get(eventType)?.push(listener as EventListener<Events, keyof Events>);
  }

  /**
   * Dispatches an event to all registered listeners.
   */
  dispatch<T extends keyof Events>(eventType: T, data: Events[T]): void {
    const eventListeners = this.#listeners.get(eventType);

    if (!eventListeners || eventListeners.length === 0) {
      return;
    }

    eventListeners.forEach((fn) => {
      try {
        (fn as Listener<Events[T]>)(data);
      } catch (e) {
        captureException(e);
      }
    });
  }

  /**
   * Removes a specific listener for an event.
   */
  remove<T extends keyof Events>(eventType: T, listener: Listener<Events[T]>): void {
    const eventListeners = this.#listeners.get(eventType);

    if (!eventListeners) {
      return;
    }

    const index = eventListeners.indexOf(listener as EventListener<Events, keyof Events>);

    if (index !== -1) {
      eventListeners.splice(index, 1);
    }
  }

  /**
   * Clears all listeners for all events.
   */
  clear(): void {
    this.#listeners.clear();
  }
}
